import React, { useState, useEffect } from "react";
import axiosInstance from "../axiosInstance";
import { FaSearch, FaTrash, FaEye } from "react-icons/fa";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import AdLayout from "../components/AdLayout";
import DataTable from "../components/ui/DataTable";
import { Modal, Button } from "react-bootstrap";
import { fetchWebsiteContactPermissions } from "../libs/customerCreatePermission";
import "../css/contactExploration.css";

const ContactExploration = () => {
  const [pageData, setPageData] = useState({ content: [], totalPages: 0, totalElements: 0 });
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [featurePerm, setFeaturePerm] = useState({
    loaded: false,
    canView: false,
    canCreate: false,
    canEdit: false,
    canDelete: false,
  });

  useEffect(() => {
    const loadPerm = async () => {
      const p = await fetchWebsiteContactPermissions(axiosInstance);
      setFeaturePerm({
        loaded: true,
        canView: Boolean(p?.canView),
        canCreate: Boolean(p?.canCreate),
        canEdit: Boolean(p?.canEdit),
        canDelete: Boolean(p?.canDelete),
      });
    };
    loadPerm();
  }, []);

  const loadContacts = async () => {
    if (!featurePerm.loaded || !featurePerm.canView) return;
    if (isLoading) return;
    setIsLoading(true);
    try {
      const params = {
        keyword: searchQuery,
        page: currentPage,
        size: itemsPerPage,
      };
      const response = await axiosInstance.get("/api/contact-exploration/search", { params });
      setPageData(response.data);
    } catch (error) {
      console.error("Error loading contacts:", error);
      toast.error("Có lỗi khi tải dữ liệu", { position: "top-right" });
      setPageData({ content: [], totalPages: 0, totalElements: 0 });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, featurePerm.loaded, featurePerm.canView]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearch = () => {
    if (!featurePerm.canView) return;
    setCurrentPage(0);
    loadContacts();
  };

  const handlePageChange = (page) => {
    setCurrentPage(page - 1);
  };

  const handleChangeItemsPerPage = (e) => {
    setItemsPerPage(parseInt(e.target.value, 10));
    setCurrentPage(0);
  };

  const handleDelete = async (id) => {
    if (!featurePerm.canDelete) {
      toast.error("Bạn không có quyền xóa dữ liệu này", { position: "top-right" });
      return;
    }
    if (window.confirm("Bạn có chắc chắn muốn xóa liên hệ này?")) {
      try {
        await axiosInstance.delete(`/api/contact-exploration/delete/${id}`);
        toast.success("Xóa thành công", { position: "top-right" });
        loadContacts();
      } catch (error) {
        console.error("Error deleting contact:", error);
        toast.error("Có lỗi khi xóa liên hệ", { position: "top-right" });
      }
    }
  };

  const handleViewDetail = (contact) => {
    if (!featurePerm.canView) return;
    setSelectedContact(contact);
    setShowDetailModal(true);
  };

  const handleCloseModal = () => {
    setShowDetailModal(false);
    setSelectedContact(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString("vi-VN", { timeZone: "Asia/Ho_Chi_Minh" });
  };

  const getStatusLabel = (status) => {
    return status === "confirmed" ? "Đã xác nhận" : "Chưa xác nhận";
  };

  const getStatusBadgeClass = (status) => {
    return status === "confirmed" ? "bg-success" : "bg-warning text-dark";
  };

  const handleConfirm = async () => {
    if (!featurePerm.canEdit) {
      toast.error("Bạn không có quyền cập nhật dữ liệu này", { position: "top-right" });
      return;
    }
    if (!selectedContact?.id || selectedContact?.status === "confirmed" || isConfirming) return;

    setIsConfirming(true);
    try {
      const response = await axiosInstance.put(
        `/api/contact-exploration/update/${selectedContact.id}`,
        { status: "confirmed" },
      );

      const updatedContact = response?.data?.data || {
        ...selectedContact,
        status: "confirmed",
      };

      setSelectedContact(updatedContact);
      setPageData((prev) => ({
        ...prev,
        content: (prev.content || []).map((item) =>
          item.id === updatedContact.id ? { ...item, status: updatedContact.status } : item,
        ),
      }));

      toast.success("Đã xác nhận yêu cầu thành công", { position: "top-right" });
    } catch (error) {
      console.error("Error confirming contact request:", error);
      toast.error("Có lỗi khi xác nhận yêu cầu", { position: "top-right" });
    } finally {
      setIsConfirming(false);
    }
  };

  const renderPagination = () => {
    const pages = [];
    const totalPages = pageData.totalPages || 0;
    const maxPagesToShow = 5;
    let startPage = Math.max(0, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(0, endPage - maxPagesToShow + 1);
    }

    if (totalPages > 0) {
      if (currentPage > 0) {
        pages.push(
          <li key="prev" className="page-item">
            <button className="page-link" onClick={() => handlePageChange(currentPage)}>
              Trước
            </button>
          </li>
        );
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(
          <li key={i} className={`page-item ${currentPage === i ? "active" : ""}`}>
            <button className="page-link" onClick={() => handlePageChange(i + 1)}>
              {i + 1}
            </button>
          </li>
        );
      }

      if (currentPage < totalPages - 1) {
        pages.push(
          <li key="next" className="page-item">
            <button className="page-link" onClick={() => handlePageChange(currentPage + 2)}>
              Sau
            </button>
          </li>
        );
      }
    }

    return pages;
  };

  return (
    <AdLayout>
      {featurePerm.loaded && !featurePerm.canView ? (
        <div className="container-fluid p-3 p-md-4">
          <div className="alert alert-warning mb-0">Bạn không có quyền truy cập màn hình này.</div>
        </div>
      ) : (
      <div className="contact-exploration-container p-3 p-md-4 bg-light">
        <div className="container-fluid">
          {/* Header */}
          <div className="row bg-dark mb-3 rounded">
            <div className="col-12 col-md-4 pt-2 pb-2">
              <h5 className="text-white mb-0 text-uppercase">Quản lý liên hệ khách hàng</h5>
            </div>
            <div className="col-12 col-md-5 pt-2 pb-2">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Tìm kiếm theo tên, số điện thoại, email..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                />
                <button className="btn btn-light btn-sm" onClick={handleSearch} title="Tìm kiếm">
                  <FaSearch />
                </button>
              </div>
            </div>
            <div className="col-12 col-md-3 pt-2 pb-2 text-md-end text-start">
              <span className="text-white">
                Tổng: {pageData.totalElements || 0} liên hệ
              </span>
            </div>
          </div>

          {/* Table */}
          <DataTable maxHeight="calc(100vh - 320px)" className="bg-white rounded shadow-sm">
            <table className="table table-striped table-hover align-middle mb-0">
              <thead className="table-dark">
                <tr>
                  <th style={{ width: "5%" }}>STT</th>
                  <th style={{ width: "18%" }}>Họ và tên</th>
                  <th style={{ width: "15%" }}>Số điện thoại</th>
                  <th style={{ width: "15%" }}>Email</th>
                  <th style={{ width: "20%" }}>Nội dung</th>
                  <th style={{ width: "12%" }}>Trạng thái</th>
                  <th style={{ width: "12%" }}>Thời gian</th>
                  <th style={{ width: "10%" }} className="text-center action-column">
                    Thao tác
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="8" className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : pageData.content && pageData.content.length > 0 ? (
                  pageData.content.map((contact, index) => (
                    <tr key={contact.id}>
                      <td>{currentPage * itemsPerPage + index + 1}</td>
                      <td>{contact.name}</td>
                      <td>{contact.numberPhone}</td>
                      <td>{contact.email || "N/A"}</td>
                      <td>
                        <div className="message-truncate">
                          {contact.message}
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${getStatusBadgeClass(contact.status)}`}>
                          {getStatusLabel(contact.status)}
                        </span>
                      </td>
                      <td>{formatDate(contact.createdAt)}</td>
                      <td className="text-center action-column">
                        <div className="action-buttons">
                          <button
                            className="btn btn-info btn-sm"
                            onClick={() => handleViewDetail(contact)}
                            title="Xem chi tiết"
                          >
                            <FaEye />
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDelete(contact.id)}
                            title="Xóa"
                            disabled={!featurePerm.canDelete}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center py-4">
                      Không có dữ liệu
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </DataTable>

          {/* Pagination */}
          <div className="row mt-3 g-2">
            <div className="col-12 col-md-6 mb-2 mb-md-0">
              <div className="d-flex align-items-center">
                <label className="me-2 mb-0">Hiển thị:</label>
                <select
                  className="form-select form-select-sm w-auto"
                  value={itemsPerPage}
                  onChange={handleChangeItemsPerPage}
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>
            </div>
            <div className="col-12 col-md-6">
              <nav>
                <ul className="pagination justify-content-md-end justify-content-center mb-0">{renderPagination()}</ul>
              </nav>
            </div>
          </div>
        </div>

        {/* Detail Modal */}
        <Modal show={showDetailModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Chi tiết liên hệ</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedContact && (
              <div className="contact-detail">
                <div className="row g-3 mb-3">
                  <div className="col-12 col-md-6">
                    <strong className="d-block mb-2">Họ và tên:</strong>
                    <div className="p-2 bg-light rounded">{selectedContact.name}</div>
                  </div>
                  <div className="col-12 col-md-6">
                    <strong className="d-block mb-2">Số điện thoại:</strong>
                    <div className="p-2 bg-light rounded">{selectedContact.numberPhone}</div>
                  </div>
                </div>
                <div className="row g-3 mb-3">
                  <div className="col-12 col-md-6">
                    <strong className="d-block mb-2">Email:</strong>
                    <div className="p-2 bg-light rounded">{selectedContact.email || "N/A"}</div>
                  </div>
                  <div className="col-12 col-md-6">
                    <strong className="d-block mb-2">Trạng thái:</strong>
                    <div className="p-2 bg-light rounded">
                      <span className={`badge ${getStatusBadgeClass(selectedContact.status)}`}>
                        {getStatusLabel(selectedContact.status)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="row g-3 mb-3">
                  <div className="col-12 col-md-6">
                    <strong className="d-block mb-2">Thời gian gửi:</strong>
                    <div className="p-2 bg-light rounded">{formatDate(selectedContact.createdAt)}</div>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-12">
                    <strong className="d-block mb-2">Nội dung:</strong>
                    <div className="message-content p-3 bg-light rounded">
                      {selectedContact.message}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant={selectedContact?.status === "confirmed" ? "success" : "primary"}
              onClick={handleConfirm}
              disabled={
                !featurePerm.canEdit ||
                !selectedContact ||
                selectedContact?.status === "confirmed" ||
                isConfirming
              }
            >
              {selectedContact?.status === "confirmed"
                ? "Đã xác nhận"
                : isConfirming
                  ? "Đang xác nhận..."
                  : "Xác nhận"}
            </Button>
            <Button variant="secondary" onClick={handleCloseModal}>
              Đóng
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
      )}
    </AdLayout>
  );
};

export default ContactExploration;
